import {queries} from '@/shared/integrations/zero'
import {EventLog} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

export type SystemLogStartRow = Pick<EventLog, 'id' | 'performedAt'>

export function useGetInfiniteSystemLogs({
  sort = {dir: 'desc', field: 'performedAt'},
  filters,
  multiFilters,
  limit = 25,
}: Omit<Partial<Parameters<typeof queries.eventLogs.listEvents>[0]>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters, multiFilters, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [sort?.dir, sort?.field, filters, multiFilters, limit, search]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getPageQuery: GetPageQuery<EventLog, SystemLogStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.performedAt ? {id: start.id, direction: dir, performedAt: start.performedAt} : undefined

      return {
        query: queries.eventLogs.listEvents({
          cursor,
          limit: pageLimit,
          sort,
          filters: {...filters, ...(search ? {name: search} : {})},
          multiFilters,
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  // @ts-expect-error schema type mismatch (works at runtime)
  const getSingleQuery: GetSingleQuery<EventLog> = useCallback(
    ({id}) => ({
      query: queries.eventLogs.getEventById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: EventLog): SystemLogStartRow => ({
      id: row.id,
      performedAt: row.performedAt,
    }),
    []
  )

  const transformToSelectItem = useCallback((row: EventLog) => ({label: row.event, value: row.id}), [])

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
